import { createContext, useContext, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { demoRootOrg, roles, users } from "../../data-mock/InitData";
import { ConfigPageIndex } from "../../domain/entities/Configs";
import { StateStatus } from "../../domain/entities/State";
import {
    getOrganizationByURl,
    getOrganizationLogin,
    loadConfigs,
} from "../redux/actions/administrator";
import {
    cognitoDecode as cognitoDecodeAction,
    sessionCognitoLogin,
    login as sessionLogin,
    loginExternal as sessionLoginExternal,
    setLoginError,
} from "../redux/actions/session";
import useCookies from "./useCookies";
import { useLocalStorage } from "./useLocalStorage";
import { extractCognitoUsername, refreshCognitoTokens } from "../utils/cognitoRefresh";

const AuthContext = createContext(null);

// Decode JWT token to get expiration time
const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useLocalStorage("user", null);
  const [userOrgs, setUserOrgs] = useLocalStorage("user-org", null);

  const signatory = useSelector((state) => state.signatory);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { removeCookie, getCookie, setCookie } = useCookies();

  const loggedUser = useSelector((state) => state.session.loggedUser);
  const loggedUserArray = useSelector((state) => state.session.loggedUserArray);
  const loggedUserArrayFull = useSelector(
    (state) => state.administrator.userOrgs
  );
  const userOrgsLoadStatus = useSelector(
    (state) => state.administrator.userOrgsLoadStatus
  );
  const organizationSave = useSelector(
    (state) => state.administrator.organizationSave
  );
  const loginStatus = useSelector((state) => state.session.loginStatus);
  const cognitoAuthLoginStatus = useSelector(
    (state) => state.session.cognitoAuthLoginStatus
  );
  const signatoryLoadStatus = useSelector(
    (state) => state.signatory.signatoryLoadStatus
  );
  const redirectProcessedRef = useRef(false);
  const siteConfigs = useSelector((state) => state.administrator.siteConfigs);
  const tokenRefreshTimerRef = useRef(null);

  // Automatic token refresh - check token expiration and refresh before it expires
  useEffect(() => {
    const checkAndRefreshToken = () => {
      const idToken = getCookie("id_token");
      
      if (!idToken || !user) {
        // Clear any existing timer if no token or user
        if (tokenRefreshTimerRef.current) {
          clearTimeout(tokenRefreshTimerRef.current);
          tokenRefreshTimerRef.current = null;
        }
        return;
      }

      const decoded = decodeToken(idToken);
      if (!decoded || !decoded.exp) {
        console.warn('Unable to decode token or missing expiration');
        return;
      }

      // Signatory tokens use a dedicated renewal endpoint instead of Cognito refresh
      if (decoded.type === 'SIGNATORY') {
        const timeUntilExpiration = decoded.exp - Math.floor(Date.now() / 1000);
        const renewWhenSecondsLeft = 30 * 60; // renew 30 min before expiry
        const timeUntilRenew = timeUntilExpiration - renewWhenSecondsLeft;

        if (tokenRefreshTimerRef.current) {
          clearTimeout(tokenRefreshTimerRef.current);
        }

        const doRenew = async () => {
          try {
            const host = process.env.REACT_APP_BACKEND;
            const resp = await fetch(`${host}/api/ext/signatory/renew-token`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${getCookie("id_token")}`,
                'Content-Type': 'application/json',
              },
            });
            if (resp.ok) {
              const data = await resp.json();
              if (data.id_token) {
                setCookie("id_token", data.id_token, { expires: 1 });
                console.log('Signatory token renewed');
              }
            }
          } catch { /* renewal is best-effort */ }
        };

        if (timeUntilRenew <= 0) {
          doRenew();
        } else {
          tokenRefreshTimerRef.current = setTimeout(doRenew, timeUntilRenew * 1000);
        }
        return;
      }

      const currentTime = Math.floor(Date.now() / 1000);
      const expirationTime = decoded.exp;
      const timeUntilExpiration = expirationTime - currentTime;

      // Refresh token 5 minutes (300 seconds) before expiration
      const refreshBeforeExpiry = 300;
      const timeUntilRefresh = timeUntilExpiration - refreshBeforeExpiry;

      console.log(`Token expires in ${Math.floor(timeUntilExpiration / 60)} minutes. Will refresh in ${Math.floor(timeUntilRefresh / 60)} minutes.`);

      // Clear any existing timer
      if (tokenRefreshTimerRef.current) {
        clearTimeout(tokenRefreshTimerRef.current);
      }

      // If token is about to expire or already expired, refresh immediately
      if (timeUntilRefresh <= 0) {
        console.log('Token expiring soon or expired, refreshing now...');
        refreshToken();
      } else {
        // Schedule refresh before token expires
        tokenRefreshTimerRef.current = setTimeout(() => {
          console.log('Scheduled token refresh triggered');
          refreshToken();
        }, timeUntilRefresh * 1000);
      }
    };

    // Check token on mount and when user changes
    checkAndRefreshToken();

    // Also check every 5 minutes as a safety net
    const intervalId = setInterval(checkAndRefreshToken, 5 * 60 * 1000);

    // Refresh immediately when the user returns to the tab (timers are throttled in background tabs)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkAndRefreshToken();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Cleanup on unmount
    return () => {
      if (tokenRefreshTimerRef.current) {
        clearTimeout(tokenRefreshTimerRef.current);
      }
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user, getCookie]);

  useEffect(() => {
    //need to add logic here so that if document belongs to same email, let them see it?
    const redirectURL = sessionStorage.getItem("redirectURL");

    if (
      user != null &&
      signatory &&
      signatoryLoadStatus === StateStatus.COMPLETE
    ) {
      // Check if the signatory ID has changed
      if (user?.id !== signatory?.signatory?.id && user.email !== signatory?.signatory.email) {
        logout();
        return;
      }
    }

    if (
      !redirectProcessedRef.current &&
      signatory &&
      signatoryLoadStatus === StateStatus.COMPLETE &&
      (user == null || user.email !== signatory.signatory?.email)
    ) {
      const newUser = {
        //converting a signatory to user so that login mechanism works with existing user mechanism.
        id: signatory.signatory.id,
        email: signatory.signatory.email,
        firstName: signatory.signatory.firstName,
        lastName: signatory.signatory.lastName,
        organization: signatory.signatory.organization,
        role: signatory.signatory.role,
        username: signatory.signatory.username,
      };
      setUser(newUser);
      setUserOrgs(newUser.organization);
      if (redirectURL) {
        redirectProcessedRef.current = true; // Mark redirect as processed
        navigate(redirectURL);
      }
    }
  }, [signatory, signatoryLoadStatus]);

  useEffect(() => {
    if (!redirectProcessedRef.current && loginStatus === StateStatus.COMPLETE) {
      const redirectUrl = sessionStorage.getItem("redirectURL");
      setUser(loggedUser);
      setUserOrgs(loggedUserArray); //this was the original and added below dispatch to refresh orgs more consistently.
      // Force refresh organizations from backend
      //dispatch(loadOrganizations(loggedUser));
      // After organizations are loaded, update localStorage
      // This will be handled by the userOrgsLoadStatus effect below
      if (redirectUrl && redirectUrl === "/?") {
        dispatch(
          loadConfigs(
            loggedUser.organization.id,
            ConfigPageIndex.CONFIGURATIONS
          )
        );
      } else {
        redirectProcessedRef.current = true;
        sessionStorage.removeItem("redirectURL");
        if (redirectUrl) {
          navigate(redirectUrl);
        } else {
          navigate("/", { replace: true });
        }
      }
    }
  }, [loggedUser, loginStatus]);

  useEffect(() => {
    if (siteConfigs?.siteConfigs && !redirectProcessedRef.current) {
      const redirectUrl = sessionStorage.getItem("redirectURL");
      if (redirectUrl === "/?") {
        redirectProcessedRef.current = true;
        const homePage = siteConfigs?.siteConfigs?.homePage;
        const homePageRoutes = {
          "Parents Communications": "/campaigns",
          "Parents Messages": "/messages",
          "Staff Communications": "/campaignsStaff",
          "Staff Messages": "/messagesStaff",
          Dashboard: "/dashboard",
        };

        const route = homePageRoutes[homePage] || "/";
        navigate(route);
      }
    }
  }, [siteConfigs]);

  useEffect(() => {
    if (loggedUserArray) {
      loggedUserArray.map((user) => {
        const orgId = user.organization.id;
        dispatch(getOrganizationLogin(orgId, loggedUserArray));
      });
    } else if (organizationSave === StateStatus.COMPLETE) {
      const orgId = user.organization?.id;
      if (orgId) dispatch(getOrganizationLogin(orgId, [user]));
    }
  }, [loggedUserArray, organizationSave]);

  useEffect(() => {
    if (userOrgsLoadStatus === StateStatus.COMPLETE) {
      setUserOrgs(loggedUserArrayFull);
    }
  }, [loggedUserArrayFull]);

  useEffect(() => {
    if (cognitoAuthLoginStatus === StateStatus.COMPLETE) {
      const idToken = getCookie("id_token");
      const authTokens = { id_token: idToken };

      const fetchUser = async () => {
        try {
          // FIX: Send token in POST body as form data, not as query param
          const tokenUrlencoded = new URLSearchParams();
          tokenUrlencoded.append("token", JSON.stringify(authTokens));

          const loggedUser = await dispatch(
            cognitoDecodeAction(tokenUrlencoded)
          );

          if (Array.isArray(loggedUser) && loggedUser.length) {
            loggedUser.map((usr) => {
              usr.role = usr.role || roles[0];
              usr.organization = usr.organization || demoRootOrg;
            });
            await cognitoLogin(loggedUser);
          } else {
            navigate("/", { replace: true });
          }
          } catch (error) {
            console.error("Error getting logged user data.", error);
            
            const errorMessage = error?.message || "";
            const parts = errorMessage.split(" - ");
            const errorCode = parts.length === 2 ? parts[1].trim() : null;

            if (errorCode === "ORG_EXPIRED_TRIAL") {
              dispatch(setLoginError("ORG_EXPIRED_TRIAL"));
            } else {
              dispatch(setLoginError("UNKNOWN_ERROR"));
              throw new Error("Error getting logged user data.");
            }
          }
      };
      fetchUser();
    }
  }, [cognitoAuthLoginStatus]);

  // call this function when you want to authenticate the user
  const login = async (data) => {
    if (data.username === users[0].email) {
      const baseURL = window.location.hostname;
      const cleanDomain = baseURL.replace(/^(https?:\/\/)?(www\.)?/, "");
      dispatch(getOrganizationByURl(cleanDomain));
    }
    dispatch(sessionLogin(data.password));
  };

  const loginExternal = async (data) => {
    const params = new URLSearchParams(location.search);
    const shortId = params.get("doc") || sessionStorage.getItem("shortId");

    return dispatch(sessionLoginExternal(shortId, data.password));
  };

  const cognitoLogin = async (data) => {
    // Set user and orgs locally for immediate state update
    if (Array.isArray(data) && data.length > 0) {
      setUser(data[0]);
      setUserOrgs(data);
    }
    await dispatch(sessionCognitoLogin(data));
    // Handle redirect after Cognito login
    const redirectUrl = sessionStorage.getItem("redirectURL");
    if (redirectUrl) {
      sessionStorage.removeItem("redirectURL");
      navigate(redirectUrl === "/?/" ? "/" : redirectUrl);
    } else {
      navigate("/", { replace: true });
    }
  };

  const switchOrganization = async (newUser) => {
    setUser(newUser);
  };

  const refreshToken = async () => {
    const refresh_token = getCookie("refresh_token");
    if (!refresh_token) {
      console.warn('No refresh token available — logging out');
      logout();
      return;
    }
    const sub = extractCognitoUsername();
    if (!sub) {
      console.warn('Cannot extract sub from id_token — logging out');
      logout();
      return;
    }
    const host = process.env.REACT_APP_BACKEND;
    const tokens = await refreshCognitoTokens(host, refresh_token, sub);
    if (tokens) {
      console.log('Token refreshed successfully');
    } else {
      console.error('Token refresh failed — logging out');
      logout();
    }
  };

  // call this function to sign out logged in user
  const logout = () => {
    const params = new URLSearchParams(location.search);
    const shortId = params.get("doc") || sessionStorage.getItem("shortId");
    if (shortId && shortId.startsWith("s")) {
      navigate(`/loginExternal?doc=${shortId}`);
    } else if (shortId && shortId.startsWith("i")) {
      navigate("/login");
    } else {
      navigate("/", { replace: true });
    }
    setUser(null);
    setUserOrgs(null);
    removeCookie("id_token");
    removeCookie("refresh_token");
    removeCookie("auth_username");
  };

  const value = useMemo(
    () => ({
      user,
      userOrgs,
      login,
      loginExternal,
      logout,
      cognitoLogin,
      switchOrganization,
      refreshToken,
    }),
    [user]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
};
